import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { pedirTextoIA } from '../utils/aiClient';
import {
  Megaphone, Sparkles, Trash2, Instagram, Search as SearchIcon,
  Target, Wallet, CalendarDays, Image as ImageIcon, Video, FileText, History, X,
} from 'lucide-react';

// Llamada autenticada a la API interna
async function api(method, path, body) {
  const token = localStorage.getItem('nebb_token');
  const res = await fetch(`/api${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const fmtMonth = (key) => {
  if (!key) return '';
  const [y, m] = key.split('-');
  return `${MESES[parseInt(m, 10) - 1]} ${y}`;
};
const fmtUYU = (n) => '$ ' + Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: 0 });

// Construye el prompt GENÉRICO de captación fría (NO usa la cartera del usuario)
function buildPrompt({ monthLabel, budget, videos, photos }) {
  return `Sos un estratega de marketing digital experto en el mercado de SEGUROS de Uruguay. Trabajás para NEBB Brokers, una corredora de seguros uruguaya. Tu objetivo es CAPTAR CLIENTES NUEVOS (captación en frío de personas que todavía NO son clientes), no fidelizar a los actuales.

Generá un plan de marketing COMPLETO para el mes de ${monthLabel} con estos parámetros REALES que no podés exceder:
- Presupuesto publicitario del mes: ${fmtUYU(budget)} pesos uruguayos (UYU).
- Canales: SOLAMENTE Instagram (Meta Ads + orgánico) y Google Ads (red de búsqueda). No propongas otros canales.
- Capacidad de producción de VIDEO este mes: ${videos} video(s). NO planifiques más videos que esa cantidad en todo el mes.
- Capacidad de FOTOS/placas este mes: ${photos} pieza(s) visual(es) fija(s). NO excedas esa cantidad.
- El resto del calendario debe llenarse con piezas que NO requieren producción nueva: placas de solo texto, carruseles armables con material existente, historias simples, anuncios de búsqueda de Google (que son solo texto).

IMPORTANTE: el usuario tiene banco propio de imágenes y video, así que para cada pieza visual indicá QUÉ buscar en el banco o QUÉ filmar (descripción concreta), no generes imágenes.

Respondé ÚNICAMENTE con un objeto JSON válido (sin texto alrededor, sin markdown, sin \`\`\`) con esta estructura EXACTA:
{
  "estrategia": {
    "publicoObjetivo": "descripción del público de captación fría a apuntar este mes (perfil demográfico, intereses, momento de vida) — mercado uruguayo de seguros, genérico",
    "anguloCampania": "el ángulo / gran idea de la campaña del mes",
    "mensajeCentral": "el mensaje central en una o dos frases"
  },
  "presupuesto": {
    "moneda": "UYU",
    "total": ${budget},
    "asignacion": [
      { "canal": "Instagram (Meta Ads)", "monto": 0, "porcentaje": 0, "racional": "por qué este monto y a qué apuntar" },
      { "canal": "Google Ads (búsqueda)", "monto": 0, "porcentaje": 0, "racional": "por qué este monto y a qué apuntar" }
    ],
    "notas": "recomendaciones de optimización del gasto"
  },
  "google": {
    "keywords": ["lista de palabras clave de búsqueda para captar (ej. seguro auto montevideo, cotizar seguro hogar, etc.)"],
    "anuncios": [
      { "titulo1": "máx 30 caracteres", "titulo2": "máx 30 caracteres", "descripcion": "máx 90 caracteres" }
    ]
  },
  "instagram": {
    "copysPosts": [
      { "tipo": "post/reel/historia/carrusel", "texto": "el copy listo para publicar", "hashtags": "los hashtags sugeridos" }
    ]
  },
  "calendario": [
    { "semana": 1, "dia": "Lunes", "canal": "Instagram", "formato": "video/foto/placa de texto/carrusel/historia/anuncio Google", "requiereProduccion": true, "tema": "de qué trata la pieza", "copy": "texto que la acompaña", "visual": "qué buscar en el banco o qué filmar/armar" }
  ],
  "resumenProduccion": { "videosPlanificados": 0, "fotosPlanificadas": 0 }
}

Reglas finales:
- videosPlanificados NO puede ser mayor que ${videos}. fotosPlanificadas NO puede ser mayor que ${photos}.
- El calendario debe ser realista para un mes (no satures: calidad sobre cantidad).
- Todo en español rioplatense, tono profesional pero cercano.`;
}

// Intenta parsear el JSON aunque venga con texto/markdown alrededor
function parsePlan(text) {
  let clean = (text || '').replace(/```json|```/g, '').trim();
  const m = clean.match(/\{[\s\S]*\}/);
  if (m) clean = m[0];
  return JSON.parse(clean);
}

export default function AgentPage() {
  const { currentUser, showToast } = useApp();
  const isAdmin = currentUser?.role === 'admin';

  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const [month, setMonth] = useState(defaultMonth);
  const [budget, setBudget] = useState('');
  const [videos, setVideos] = useState('1');
  const [photos, setPhotos] = useState('4');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState(null);     // plan actualmente mostrado
  const [history, setHistory] = useState([]); // planes guardados
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { if (isAdmin) cargarHistorial(); }, [isAdmin]);

  const cargarHistorial = async () => {
    try {
      const planes = await api('GET', '/marketing-plans');
      setHistory(planes);
      // Rehidratar: al entrar (o recargar la pantalla), mostrar automáticamente
      // el plan del mes actual si existe; si no, el más reciente. Así nunca se
      // "pierde" lo generado al refrescar.
      if (!plan && planes.length > 0) {
        const delMes = planes.find(p => p.month === month);
        setPlan(delMes || planes[0]);
        if (delMes || planes[0]) {
          const base = delMes || planes[0];
          setMonth(base.month || month);
          setBudget(String(base.budget || ''));
          setVideos(String(base.videos ?? '1'));
          setPhotos(String(base.photos ?? '4'));
        }
      }
    } catch (e) { /* silencioso */ }
  };

  const generar = async () => {
    setError('');
    const budgetNum = parseInt(String(budget).replace(/\D/g, ''), 10);
    if (!budgetNum || budgetNum <= 0) { setError('Ingresá un presupuesto válido en pesos.'); return; }
    const v = parseInt(videos, 10) || 0;
    const f = parseInt(photos, 10) || 0;

    setLoading(true);
    try {
      const prompt = buildPrompt({ monthLabel: fmtMonth(month), budget: budgetNum, videos: v, photos: f });
      const { texto: text } = await pedirTextoIA({ content: prompt, model: 'claude-sonnet-4-5', maxTokens: 8000 });
      let parsed;
      try { parsed = parsePlan(text); }
      catch { throw new Error('La IA devolvió un formato inesperado. Probá de nuevo.'); }

      const nuevo = { month, budget: budgetNum, videos: v, photos: f, data: parsed };
      // Guardar
      const guardado = await api('POST', '/marketing-plans', nuevo);
      setPlan(guardado);
      setHistory(prev => [guardado, ...prev]);
      showToast && showToast('Plan generado y guardado', 'success');
    } catch (e) {
      setError(e.message || 'Error al generar el plan.');
    } finally {
      setLoading(false);
    }
  };

  const borrar = async (id) => {
    if (!window.confirm('¿Eliminar este plan del historial?')) return;
    try {
      await api('DELETE', `/marketing-plans/${id}`);
      setHistory(prev => prev.filter(p => p.id !== id));
      if (plan?.id === id) setPlan(null);
    } catch (e) { showToast && showToast('Error al eliminar', 'error'); }
  };

  if (!isAdmin) {
    return (
      <div className="page fade-in">
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <Megaphone size={32} color="var(--text-muted)" style={{ marginBottom: 12 }} />
          <h2 style={{ marginBottom: 8 }}>Acceso restringido</h2>
          <p style={{ color: 'var(--text-secondary)' }}>El Agente de Marketing está disponible solo para el CEO / administrador.</p>
        </div>
      </div>
    );
  }

  const d = plan?.data;

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title"><Megaphone size={22} style={{ display: 'inline', marginRight: 8, verticalAlign: '-3px', color: 'var(--accent)' }} />Agente de Marketing</h1>
          <p className="page-subtitle">Campañas de captación de clientes nuevos · Instagram y Google · presupuesto en pesos</p>
        </div>
        <button className="btn btn-secondary" onClick={() => setShowHistory(true)}>
          <History size={14} /> Historial ({history.length})
        </button>
      </div>

      {/* Inputs del mes */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, marginBottom: 16, color: 'var(--text-secondary)' }}>PARÁMETROS DEL MES</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 5 }}>Mes</label>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 5 }}><Wallet size={11} style={{ display: 'inline', marginRight: 4 }} />Presupuesto (UYU)</label>
            <input type="text" inputMode="numeric" value={budget} onChange={e => setBudget(e.target.value)} placeholder="ej. 30000" style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 5 }}><Video size={11} style={{ display: 'inline', marginRight: 4 }} />Videos que puedo hacer</label>
            <input type="number" min="0" value={videos} onChange={e => setVideos(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 5 }}><ImageIcon size={11} style={{ display: 'inline', marginRight: 4 }} />Fotos/placas que puedo armar</label>
            <input type="number" min="0" value={photos} onChange={e => setPhotos(e.target.value)} style={inputStyle} />
          </div>
        </div>
        {error && <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--red-dim)', color: 'var(--red)', borderRadius: 8, fontSize: 13 }}>{error}</div>}
        <button className="btn btn-primary" onClick={generar} disabled={loading} style={{ marginTop: 16 }}>
          {loading
            ? <><span className="spinner" style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(0,0,0,0.2)', borderTopColor: '#fff', borderRadius: '50%' }} /> Generando plan...</>
            : <><Sparkles size={15} /> Generar plan del mes</>}
        </button>
      </div>

      {/* Plan generado */}
      {d && (
        <div className="fade-in">
          {/* Estrategia */}
          <div className="card" style={{ marginBottom: 16 }}>
            <SectionTitle icon={Target} text="ESTRATEGIA" />
            <Field label="Público objetivo" value={d.estrategia?.publicoObjetivo} />
            <Field label="Ángulo de campaña" value={d.estrategia?.anguloCampania} />
            <Field label="Mensaje central" value={d.estrategia?.mensajeCentral} />
          </div>

          {/* Presupuesto */}
          <div className="card" style={{ marginBottom: 16 }}>
            <SectionTitle icon={Wallet} text={`PRESUPUESTO · ${fmtUYU(plan.budget)}`} />
            {(d.presupuesto?.asignacion || []).map((a, i) => (
              <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <strong style={{ fontSize: 14 }}>{a.canal}</strong>
                  <span style={{ color: 'var(--accent)', fontWeight: 600, fontFamily: 'var(--font-display)' }}>{fmtUYU(a.monto)} {a.porcentaje ? `(${a.porcentaje}%)` : ''}</span>
                </div>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>{a.racional}</p>
              </div>
            ))}
            {d.presupuesto?.notas && <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10, fontStyle: 'italic' }}>{d.presupuesto.notas}</p>}
          </div>

          {/* Google */}
          <div className="card" style={{ marginBottom: 16 }}>
            <SectionTitle icon={SearchIcon} text="GOOGLE ADS (BÚSQUEDA)" />
            {d.google?.keywords?.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Palabras clave</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {d.google.keywords.map((k, i) => <span key={i} className="badge" style={{ background: 'var(--blue-dim)', color: 'var(--blue)' }}>{k}</span>)}
                </div>
              </div>
            )}
            {(d.google?.anuncios || []).map((a, i) => (
              <div key={i} style={{ padding: 12, background: 'var(--bg-elevated)', borderRadius: 8, marginBottom: 8 }}>
                <div style={{ color: 'var(--blue)', fontSize: 14, fontWeight: 500 }}>{a.titulo1} · {a.titulo2}</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{a.descripcion}</div>
              </div>
            ))}
          </div>

          {/* Instagram */}
          <div className="card" style={{ marginBottom: 16 }}>
            <SectionTitle icon={Instagram} text="INSTAGRAM · COPYS" />
            {(d.instagram?.copysPosts || []).map((c, i) => (
              <div key={i} style={{ padding: 12, background: 'var(--bg-elevated)', borderRadius: 8, marginBottom: 8 }}>
                <span className="badge" style={{ textTransform: 'capitalize', marginBottom: 6, display: 'inline-block' }}>{c.tipo}</span>
                <p style={{ fontSize: 13, margin: '4px 0', whiteSpace: 'pre-wrap' }}>{c.texto}</p>
                {c.hashtags && <p style={{ fontSize: 12, color: 'var(--accent)', margin: 0 }}>{c.hashtags}</p>}
              </div>
            ))}
          </div>

          {/* Calendario */}
          <div className="card">
            <SectionTitle icon={CalendarDays} text="CALENDARIO DEL MES" />
            {d.resumenProduccion && (
              <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 12, color: 'var(--text-muted)' }}>
                <span><Video size={11} style={{ display: 'inline', marginRight: 4 }} />{d.resumenProduccion.videosPlanificados}/{plan.videos} videos</span>
                <span><ImageIcon size={11} style={{ display: 'inline', marginRight: 4 }} />{d.resumenProduccion.fotosPlanificadas}/{plan.photos} fotos</span>
              </div>
            )}
            <div className="table-wrap">
              <table>
                <thead><tr><th>Sem</th><th>Día</th><th>Canal</th><th>Formato</th><th>Tema</th><th>Copy</th><th>Visual (banco/producción)</th></tr></thead>
                <tbody>
                  {(d.calendario || []).map((c, i) => (
                    <tr key={i}>
                      <td>{c.semana}</td>
                      <td>{c.dia}</td>
                      <td>{c.canal}</td>
                      <td>
                        <span className="badge" style={{ background: c.requiereProduccion ? 'var(--amber-dim, rgba(245,166,35,0.12))' : 'var(--bg-elevated)', color: c.requiereProduccion ? 'var(--amber)' : 'var(--text-secondary)' }}>
                          {c.formato}
                        </span>
                      </td>
                      <td style={{ fontSize: 12 }}>{c.tema}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 220, whiteSpace: 'pre-wrap' }}>{c.copy}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 200 }}>{c.visual}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!d && !loading && (
        <div className="card" style={{ textAlign: 'center', padding: 36, color: 'var(--text-muted)' }}>
          <FileText size={28} style={{ marginBottom: 10 }} />
          <p>Cargá el presupuesto y tu capacidad de producción del mes, y generá el plan.</p>
        </div>
      )}

      {/* Historial */}
      {showHistory && (
        <div className="modal-overlay" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 40, overflowY: 'auto' }}>
          <div className="card" style={{ maxWidth: 560, width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Historial de planes</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowHistory(false)}><X size={14} /></button>
            </div>
            {history.length === 0 && <p style={{ color: 'var(--text-muted)' }}>Todavía no hay planes guardados.</p>}
            {history.map(h => (
              <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ flex: 1 }}>
                  <strong style={{ textTransform: 'capitalize' }}>{fmtMonth(h.month)}</strong>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtUYU(h.budget)} · {h.videos} videos · {h.photos} fotos</div>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={() => { setPlan(h); setShowHistory(false); }}>Ver</button>
                <button className="btn btn-danger btn-sm" onClick={() => borrar(h.id)}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '9px 12px', background: 'var(--bg-elevated)',
  border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14,
};

function SectionTitle({ icon: Icon, text }) {
  return (
    <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, marginBottom: 14, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 7 }}>
      <Icon size={15} color="var(--accent)" /> {text}
    </h3>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 3 }}>{label}</div>
      <p style={{ fontSize: 14, margin: 0, lineHeight: 1.5 }}>{value}</p>
    </div>
  );
}
